import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {ObjectTypes, ApiErrorCodes, ApiActions, SortDirections} from '../../../api/consts';
import {useTranslation} from 'react-i18next';
import {push as navigateTo} from 'connected-react-router';
import {useGenericResultsDataApi} from '../../../api/hooks';
import {useSMCSnackbar, useColumnsWidthsPersistedState, useSort, useColumnsStatePersistedState} from '../../../utils/hooks';
import {SMCPaths} from '../../../conf/Routes';
import {CommunityFilterSchema, CommunityGetListSchema, CommunityStatuses, CommunityVpnTypes} from '../../../api/DBSchema/Community';
import store from '../../../store';
import DescriptionCell from '../../../components/core/SMCTable/CommonCells/DescriptionCell';
import EditObjectCell from '../../../components/core/SMCTable/CommonCells/EditObjectCell';
import SMCIcon from '../../../components/Simple/SMCIcon';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import Api from '../../../api/SMPFetchHelper';
import {buildStringSortCaseInsensitive} from '../../../utils/sortUtils';
import SMCHighlighter from '../../../components/Simple/SMCHighlighter';

const VpnTypeCell = React.memo(({value}) => {
    const { t } = useTranslation();
    switch (value) {
        case CommunityVpnTypes.fullMesh:
            return t('SMC_COMMUNITY_DVPN_TYPE_FULL_MESH');
        case CommunityVpnTypes.star:
            return t('SMC_COMMUNITY_DVPN_TYPE_STAR');
        default:
            return null;
    }
})

const StatusCell = React.memo(({value}) => {
    const { t } = useTranslation();
    switch (value) {
        case CommunityStatuses.ok:
            return <SMCIcon icon="greenCheck" title={t('SMC_GEN_OK')} alt="ok" />;
        case CommunityStatuses.outdated:
            return <SMCIcon icon="redX" title={t('SMP.COMMUNITIES.TABLE.GENERAL.OUTDATED')} alt="outdated" />;
        default:
            return null;
    }
});

const TopologyErrorCell = React.memo(({value, textToHighlight}) => {
    return (
        <SMCHighlighter highlight={textToHighlight} style={{ color: 'red' }} text={value} />
    );
});


export function useCommunityResults() {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSMCSnackbar();
    const { sortBasis, sortDirection, setSort } = useSort('name', SortDirections.ASCENDING);
    
    const columns = useMemo(() => ([
        { id: 'name',          accessor: CommunityGetListSchema.name,          header: t('SMP.NAME'),                                                       width: 100,                  defaultShow: true, sort: buildStringSortCaseInsensitive(CommunityGetListSchema.name),          Cell: EditObjectCell    },
        { id: 'description',   accessor: CommunityGetListSchema.description,   header: '',                                      name: t('SMP.DESCRIPTION'), width: 32,  align: 'center', defaultShow: true, sort: buildStringSortCaseInsensitive(CommunityGetListSchema.description),   Cell: DescriptionCell   },
        { id: 'vpnType',       accessor: CommunityGetListSchema.vpnType,       header: t('SMP.COMMUNITIES.TABLE.VPN_TYPE'),                                 width: 90,                   defaultShow: true, sort: buildStringSortCaseInsensitive(CommunityGetListSchema.vpnType),       Cell: VpnTypeCell       },
        { id: 'memberOf',      accessor: CommunityGetListSchema.memberOf,      header: t('SMP.COMMUNITIES.TABLE.MEMBER_OF'),                                width: 115,                  defaultShow: true, sort: buildStringSortCaseInsensitive(CommunityGetListSchema.memberOf),      Cell: EditObjectCell    },
        { id: 'status',        accessor: CommunityGetListSchema.status,        header: t('SMP.COMMUNITIES.TABLE.STATUS'),                                   width: 55,  align: 'center', defaultShow: true, sort: buildStringSortCaseInsensitive(CommunityGetListSchema.status),        Cell: StatusCell        },
        { id: 'topologyError', accessor: CommunityGetListSchema.topologyError, header: t('SMP.COMMUNITIES.TABLE.TOPOLOGY_ERR'),                             width: 200,                  defaultShow: true, sort: buildStringSortCaseInsensitive(CommunityGetListSchema.topologyError), Cell: TopologyErrorCell }
    ]), [t]);
    
    const rowIdSelector = obj => obj[CommunityGetListSchema.name];

    const defaultColumnsState = useMemo(() => (
        columns.map(column => ({
            id: column.id,
            show: Boolean(column.defaultShow)
        }))
    ), [columns]);
    const [columnsState, setColumnsState] = useColumnsStatePersistedState(ObjectTypes.COMMUNITY, defaultColumnsState);
    const setDefaultColumnsState = useCallback(() => setColumnsState(defaultColumnsState), [defaultColumnsState, setColumnsState]);

    const [columnsWidths, setColumnsWidths] = useColumnsWidthsPersistedState(ObjectTypes.COMMUNITY, null);

    const [isPageLoaded, setIsPageLoaded] = useState(false);
    const isFirstRender = useRef(true);

    const {
        tableContent,
        isFetching,
        fetchNextPage,
        isLastPage,
        reloadTable,
        reloadTableNoClearSelected,
        selectedObjects,
        setSelectedObjects,
        deleteSelectedObjects,
        filterObject,
        setFilterObject
    } = useGenericResultsDataApi(ObjectTypes.COMMUNITY);

    // Community has no freeTextSearch, uses name instead
    const freeTextSearch = useMemo(() => {
        if (filterObject && filterObject[CommunityFilterSchema.name]) {
            var nameFilterValue = filterObject[CommunityFilterSchema.name].value;
            // If filter starts and ends with '*', return without them
            if (nameFilterValue && nameFilterValue.startsWith('*') && nameFilterValue.endsWith('*')) {
                return nameFilterValue.substring(1, nameFilterValue.length-1);
            } else {
                return nameFilterValue;
            }
        }
        return null;
    }, [filterObject]);

    const newObjectCallback = useCallback(objectId => {
        if (objectId) {
            store.dispatch(navigateTo({search: 'id=' + objectId}));
        }
    }, []);

    const reloadButtonOnClick = useCallback(() => {
        reloadTable()
            .catch(errorCode => {
                enqueueErrorSnackbar(t(errorCode));
            });
    }, [reloadTable, enqueueErrorSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        if (window.confirm(t('RESULTS_OBJECTS_DELETE_QUES'))) {
            setIsPageLoaded(false);
            deleteSelectedObjects()
                    .then(({successMessage, errorMessage}) => {
                        successMessage && enqueueSuccessSnackbar(successMessage);
                        errorMessage && enqueueErrorSnackbar(errorMessage);
                        reloadTable();
                    })
                    .catch(e => {
                        const errorMessage = getErrorMessage(e);
                        enqueueErrorSnackbar(errorMessage);
                    })
                    .finally(() => {
                        setIsPageLoaded(true);
                    });
        }
    }, [selectedObjects, deleteSelectedObjects, reloadTable, enqueueSuccessSnackbar, enqueueErrorSnackbar, getErrorMessage, t]);

    const generateTopologyAction = useCallback(async () => {
        if (!Array.isArray(selectedObjects) || selectedObjects.length === 0) {
            alert(t('SMC_GEN_NO_SELECT'));
            return;
        }
        if (!window.confirm(t('RESULTS_COMMUNITIES_TOPO_GENERATE_QUES'))) {
            return;
        }
        try {
            await Api.doAction(ObjectTypes.COMMUNITY, ApiActions.community.generateTopology, selectedObjects);
            enqueueSuccessSnackbar(t('com.sofaware.db.TOPOLOGY_OK'));
            // const respDataResults = resp.data.results;
            // const successCnt = respDataResults.filter(res => res.success).length;
            // let errorCnt = respDataResults.length - successCnt;
            // if (successCnt > 0) {
            //     enqueueSuccessSnackbar(t('RESULTS_COMMUNITIES_ALL_TOPO_OK', [successCnt]));
            // }
            // if (errorCnt > 0) {
            //     enqueueErrorSnackbar(t('RESULTS_COMMUNITIES_TOPO_WITH_ERR', [errorCnt]));
            // }
        } catch (e) {
            const errorMessage = getErrorMessage(e);
            enqueueErrorSnackbar(errorMessage);
        }
    }, [selectedObjects, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    // Load table on first time loading
    useEffect(() => {
        reloadTable()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTable,  enqueueErrorSnackbar, t]);
   
    // Reload table without clearing selected on filter changed
    useEffect(() => {
        // Don't run on first render, only when filter is changed
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        reloadTableNoClearSelected()
        .catch(errorCode => {
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                store.dispatch(navigateTo(SMCPaths.logout));
            } else {
                enqueueErrorSnackbar(t(errorCode), { preventDuplicate: true })
            }
        })
        .finally(() => setIsPageLoaded(true));
    }, [reloadTableNoClearSelected, filterObject, enqueueErrorSnackbar, t]);

    return ({
        tableContent,
        isFetching,
        isPageLoaded,
        filterObject,
        setFilterObject,
        freeTextSearch,
        fetchNextPage,
        isLastPage,
        reloadTable,
        columns,
        sortBasis,
        sortDirection,
        setSort,
        columnsState,
        setColumnsState,
        setDefaultColumnsState,
        columnsWidths,
        setColumnsWidths,
        rowIdSelector,
        selectedObjects,
        setSelectedObjects,
        newObjectCallback,
        reloadButtonOnClick,
        deleteButtonOnClick,
        generateTopologyAction
    });
}